import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppType } from "../api";
import { Attachment, TriggerTypeAPI } from "../components/FileAttachments/FileAttachments";
import { appInfo, userInfo } from "../authConfig";

// Helper function to revoke blob URLs to prevent memory leaks
const revokeBlobURLs = (attachments: Array<Attachment>): void => {
    attachments.forEach(attachment => {
        if (attachment.preview && attachment.preview.startsWith("blob:")) {
            URL.revokeObjectURL(attachment.preview);
        }
    });
};

interface QuestionInputState {
    templateAppliedFlag: boolean;
    questionInputUndoHistoryStack: Array<string>;
    questionInputRedoHistoryStack: Array<string>;
    // focus position to retain after cut operation.
    focusPositionToRetain: number | null;
    answerGenerationActiveDetails: AnswerGenerationActiveDetails;
    answerGenerationActive: boolean;
    requestedToForceStopAIResponseGeneration: boolean;
    requestTriggerType: RequestTriggerType;
    abortStageLevel: AbortStageLevel;
    isSidebarOpen: boolean;
    fileAttachments: Array<Attachment>;
    isSessionSearchActive: boolean;
    serverSideFileAttachmentsIds: Array<FileAttachmentDetails>;
    fileUploadOrDeleteAPITriggerActive: { triggerActive: boolean; triggerAPI: TriggerTypeAPI | null; error?: string };
    activeFileAttachmentTour: boolean;
}

export type RequestTriggerType = "QuestionInput" | "QuestionEdit" | "TabSwitch" | "RegenerateClick" | null;

export type AbortStageLevel = "RequestInitiated" | "ResponseReceived" | "ResponseStreaming" | null;

export interface FileAttachmentDetails {
    frontEndId: string;
    serverSideId: string;
    original_filename: string;
    stored_filename: string;
    preview: string;
}

export interface AnswerGenerationActiveDetails {
    isEditedQuestion: boolean;
    editedQuestionId: string | null;
    isChatShared?: boolean;
    selectedAppType: AppType;
    questionSendToAI: string;
    questionContentToReRenderInQuestionInput: string;
    sessionId: string | null;
    templateId: string | null;
    promptInstructions: string | undefined | null;
    fileAttachments: Array<Attachment>;
}

const questionInputInitialState: QuestionInputState = {
    templateAppliedFlag: false,
    questionInputUndoHistoryStack: [""],
    questionInputRedoHistoryStack: [],
    focusPositionToRetain: null,

    answerGenerationActiveDetails: {
        isEditedQuestion: false,
        editedQuestionId: null,
        isChatShared: false,
        selectedAppType: appInfo?.use_all_tab ? "ALL" : "FAQ",
        questionSendToAI: "",
        questionContentToReRenderInQuestionInput: "",
        sessionId: null,
        templateId: null,
        promptInstructions: null,
        fileAttachments: []
    },
    answerGenerationActive: false,
    requestedToForceStopAIResponseGeneration: false,
    requestTriggerType: null,
    abortStageLevel: null,
    isSidebarOpen: userInfo?.isSidebarOpen === "true" || userInfo?.isSidebarOpen === "True",
    fileAttachments: [],
    isSessionSearchActive: false,
    serverSideFileAttachmentsIds: [],
    fileUploadOrDeleteAPITriggerActive: { triggerActive: false, triggerAPI: null },
    activeFileAttachmentTour: false
};

const QuestionInputReducer = createSlice({
    name: "questionInput",
    initialState: questionInputInitialState,
    reducers: {
        updateTemplateAppliedFlag: (state, action: PayloadAction<boolean>) => {
            state.templateAppliedFlag = action.payload;
        },
        addToQuestionInputUndoHistoryStack(state, action: PayloadAction<string>) {
            if (state.questionInputUndoHistoryStack[state.questionInputUndoHistoryStack.length - 1] !== action.payload) {
                state.questionInputUndoHistoryStack.push(action.payload);
                state.questionInputRedoHistoryStack = [];
            }
        },
        undoEventForQuestionInput(state) {
            if (state.questionInputUndoHistoryStack.length > 1) {
                const lastState = state.questionInputUndoHistoryStack.pop();
                if (lastState) state.questionInputRedoHistoryStack.unshift(lastState);
            }
        },
        redoEventForQuestionInput(state) {
            if (state.questionInputRedoHistoryStack.length > 0) {
                const redoState = state.questionInputRedoHistoryStack.shift();
                if (redoState) state.questionInputUndoHistoryStack.push(redoState);
            }
        },
        resetQuestionInputHistoryStacks(state) {
            state.questionInputUndoHistoryStack = [...questionInputInitialState.questionInputUndoHistoryStack];
            state.questionInputRedoHistoryStack = [...questionInputInitialState.questionInputRedoHistoryStack];
            state.focusPositionToRetain = null;
        },
        updateFocusPositionToRetain(state, action: PayloadAction<number | null>) {
            state.focusPositionToRetain = action.payload;
        },

        updateAnswerGenerationActiveDetails: (state, action: PayloadAction<AnswerGenerationActiveDetails>) => {
            state.answerGenerationActiveDetails.isEditedQuestion = action.payload.isEditedQuestion;
            state.answerGenerationActiveDetails.editedQuestionId = action.payload.editedQuestionId;
            state.answerGenerationActiveDetails.isChatShared = action.payload.isChatShared ?? false;
            state.answerGenerationActiveDetails.selectedAppType = action.payload.selectedAppType;
            state.answerGenerationActiveDetails.questionSendToAI = action.payload.questionSendToAI;
            state.answerGenerationActiveDetails.questionContentToReRenderInQuestionInput = action.payload.questionContentToReRenderInQuestionInput;
            state.answerGenerationActiveDetails.sessionId = action.payload.sessionId;
            state.answerGenerationActiveDetails.templateId = action.payload.templateId;
            state.answerGenerationActiveDetails.promptInstructions = action.payload.promptInstructions;
            state.answerGenerationActiveDetails.fileAttachments = action.payload.fileAttachments;
        },
        resetAnswerGenerationActiveDetails: state => {
            // Revoke blob URLs before clearing attachments to prevent memory leaks
            revokeBlobURLs(state.fileAttachments);
            revokeBlobURLs(state.answerGenerationActiveDetails.fileAttachments);

            state.answerGenerationActiveDetails.isEditedQuestion = { ...questionInputInitialState.answerGenerationActiveDetails }.isEditedQuestion;
            state.answerGenerationActiveDetails.editedQuestionId = {
                ...questionInputInitialState.answerGenerationActiveDetails
            }.editedQuestionId;
            state.answerGenerationActiveDetails.isChatShared = {
                ...questionInputInitialState.answerGenerationActiveDetails
            }.isChatShared;
            state.answerGenerationActiveDetails.selectedAppType = { ...questionInputInitialState.answerGenerationActiveDetails }.selectedAppType;
            state.answerGenerationActiveDetails.questionSendToAI = { ...questionInputInitialState.answerGenerationActiveDetails }.questionSendToAI;
            state.answerGenerationActiveDetails.questionContentToReRenderInQuestionInput = {
                ...questionInputInitialState.answerGenerationActiveDetails
            }.questionContentToReRenderInQuestionInput;
            state.answerGenerationActiveDetails.sessionId = { ...questionInputInitialState.answerGenerationActiveDetails }.sessionId;
            state.answerGenerationActiveDetails.templateId = { ...questionInputInitialState.answerGenerationActiveDetails }.templateId;
            state.answerGenerationActiveDetails.promptInstructions = { ...questionInputInitialState.answerGenerationActiveDetails }.promptInstructions;
            state.requestedToForceStopAIResponseGeneration = false;
            state.requestTriggerType = null;
            state.answerGenerationActive = false;
            state.abortStageLevel = null;
            state.fileAttachments = [];
            state.answerGenerationActiveDetails.fileAttachments = [];
            state.activeFileAttachmentTour = false;
        },
        updateAnswerGenerationActive: (state, action: PayloadAction<boolean>) => {
            state.answerGenerationActive = action.payload;
        },
        updateForceStopAIResponseGeneration: (state, action: PayloadAction<boolean>) => {
            state.requestedToForceStopAIResponseGeneration = action.payload;
        },
        updateRequestTriggerType: (state, action: PayloadAction<RequestTriggerType>) => {
            state.requestTriggerType = action.payload;
        },
        updateAbortStageLevel: (state, action: PayloadAction<AbortStageLevel>) => {
            state.abortStageLevel = action.payload;
        },
        updateIsSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.isSidebarOpen = action.payload;
        },
        updateFileAttachments: (state, action: PayloadAction<Array<Attachment>>) => {
            state.fileAttachments = action.payload;
        },
        updateIsSearchActive: (state, action: PayloadAction<boolean>) => {
            state.isSessionSearchActive = action.payload;
        },
        updateServerSideFileAttachmentsIds: (state, action: PayloadAction<Array<FileAttachmentDetails>>) => {
            state.serverSideFileAttachmentsIds = action.payload;
        },
        updateFileUploadOrDeleteAPITriggerActive: (
            state,
            action: PayloadAction<{ triggerActive: boolean; triggerAPI: TriggerTypeAPI | null; error?: string }>
        ) => {
            state.fileUploadOrDeleteAPITriggerActive = action.payload;
        },
        updateActiveFileAttachmentTour: (state, action: PayloadAction<boolean>) => {
            state.activeFileAttachmentTour = action.payload;
        }
    }
});

export const QuestionInputReducerAction = QuestionInputReducer.actions;

export default QuestionInputReducer.reducer;
